import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { usePotmTally, useVotePotm } from '@/features/matches/potm';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FixtureWithRefs, PlayerMatchStatWithPlayer } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

/** How many candidates to show before the "show all" expander kicks in. */
const COLLAPSED_COUNT = 6;

interface Candidate {
  playerId: string;
  name: string;
  photo: string | null;
  side: 'home' | 'away';
}

function VoteRow({
  candidate,
  votes,
  total,
  mine,
  leading,
  disabled,
  onVote,
}: {
  candidate: Candidate;
  votes: number;
  total: number;
  mine: boolean;
  leading: boolean;
  disabled: boolean;
  onVote: (playerId: string) => void;
}) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const pct = total > 0 ? Math.round((votes / total) * 100) : 0;
  const accent = candidate.side === 'home' ? palette.volt : palette.captain;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: mine }}
      onPress={() => onVote(candidate.playerId)}
      disabled={disabled}
      className="active:opacity-80"
      style={{
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: mine ? accent : palette.line,
        backgroundColor: mine ? accent + '14' : palette.inset,
        overflow: 'hidden',
      }}
    >
      {/* Tally fill behind the row — a subtle live "vote share" bar. */}
      {total > 0 ? (
        <View
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            start: 0,
            width: `${pct}%`,
            backgroundColor: accent + (mine ? '22' : '14'),
          }}
        />
      ) : null}
      <View className="flex-row items-center gap-3 px-3 py-2.5">
        <PlayerAvatar uri={candidate.photo} name={candidate.name} size={36} />
        <View className="flex-1">
          <View className="flex-row items-center gap-1.5">
            <AppText variant="caption" weight="semibold" numberOfLines={1} className="flex-1 text-ink">
              {candidate.name}
            </AppText>
            {leading && total > 0 ? (
              <View className="flex-row items-center gap-0.5 rounded-full bg-volt-glass px-1.5 py-0.5">
                <Ionicons name="star" size={9} color={palette.voltBright} />
                <AppText variant="micro" weight="bold" className="text-volt-bright">
                  {pct}%
                </AppText>
              </View>
            ) : total > 0 ? (
              <AppText variant="micro" weight="bold" tabular className="text-soft">
                {pct}%
              </AppText>
            ) : null}
          </View>
          {votes > 0 ? (
            <AppText variant="micro" className="mt-0.5 text-muted">
              {votes === 1
                ? t('potm.votesOne')
                : votes === 2
                  ? t('potm.votesTwo', { count: formatNumber(votes, numeralOptions) })
                  : votes <= 10
                    ? t('potm.votesFew', { count: formatNumber(votes, numeralOptions) })
                    : t('potm.votes', { count: formatNumber(votes, numeralOptions) })}
            </AppText>
          ) : null}
        </View>
        {mine ? (
          <Ionicons name="checkmark-circle" size={20} color={accent} />
        ) : (
          <Ionicons name="ellipse-outline" size={20} color={palette.muted} />
        )}
      </View>
    </Pressable>
  );
}

/**
 * Player of the Match voting — a live/finished-only section. Shows the players
 * who featured (from the match stats), lets the user cast one vote and change it
 * while live, and renders a live community tally. Self-hides when no player
 * pool is available for the fixture.
 */
export function PotmVoting({
  fixture,
  stats,
}: {
  fixture: FixtureWithRefs;
  /** The players who featured (match stats); the candidate pool + photos. */
  stats: PlayerMatchStatWithPlayer[];
}) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const { userId } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const live = fixture.status === 'live';
  const finished = fixture.status === 'finished';
  const tallyQ = usePotmTally(fixture.id, live, live || finished);
  const vote = useVotePotm(fixture.id);

  // Candidate pool: every player with a match-stats row, sorted by rating so the
  // standouts surface first. De-duped by player id.
  const candidates = useMemo<Candidate[]>(() => {
    const seen = new Set<string>();
    const out: Candidate[] = [];
    for (const stat of stats) {
      const player = stat.player;
      if (!player || seen.has(player.id)) continue;
      seen.add(player.id);
      const side: 'home' | 'away' =
        stat.team_id === fixture.home_team?.id
          ? 'home'
          : stat.team_id === fixture.away_team?.id
            ? 'away'
            : 'home';
      out.push({
        playerId: player.id,
        name: player.known_as || player.name,
        photo: player.photo_url,
        side,
      });
    }
    return out;
  }, [stats, fixture.home_team?.id, fixture.away_team?.id]);

  const tally = useMemo(() => tallyQ.data ?? [], [tallyQ.data]);
  const votesByPlayer = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of tally) map.set(row.player_id, row.vote_count);
    return map;
  }, [tally]);
  const myVote = tally.find((row) => row.my_vote)?.player_id ?? null;
  const total = tally.reduce((sum, row) => sum + row.vote_count, 0);
  // The current leader (most votes); ties resolve to the first in the desc tally.
  const leaderId = tally.length > 0 && tally[0].vote_count > 0 ? tally[0].player_id : null;

  // Nothing to vote on — silent hide (keeps the match screen clean pre-lineups).
  if (candidates.length === 0) return null;

  const onVote = (playerId: string) => {
    if (!userId) {
      toast.show({ message: t('potm.signIn'), tone: 'info' });
      return;
    }
    vote.mutate(playerId, {
      onSuccess: () => toast.show({ message: t('potm.voted'), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  // Keep the user's pick + the leader visible even when collapsed.
  const visible = expanded
    ? candidates
    : candidates
        .slice()
        .sort((a, b) => {
          const aPin = a.playerId === myVote || a.playerId === leaderId ? 1 : 0;
          const bPin = b.playerId === myVote || b.playerId === leaderId ? 1 : 0;
          if (aPin !== bPin) return bPin - aPin;
          return (votesByPlayer.get(b.playerId) ?? 0) - (votesByPlayer.get(a.playerId) ?? 0);
        })
        .slice(0, COLLAPSED_COUNT);

  return (
    <Card className="mx-4 mb-4 gap-3 px-4 py-4">
      <View className="flex-row items-center gap-2">
        <View
          className="h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: palette.voltGlass }}
        >
          <Ionicons name="trophy" size={15} color={palette.voltBright} />
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink">
            {t('potm.title')}
          </AppText>
          <AppText variant="micro" className="text-muted">
            {finished ? t('potm.subtitleFinal') : t('potm.subtitle')}
          </AppText>
        </View>
        {total > 0 ? (
          <View className="flex-row items-center gap-1 rounded-full px-2 py-0.5" style={{ backgroundColor: palette.inset }}>
            <Ionicons name="people" size={11} color={palette.muted} />
            <AppText variant="micro" weight="bold" tabular className="text-soft">
              {formatNumber(total, numeralOptions)}
            </AppText>
          </View>
        ) : null}
      </View>

      <View className="gap-2">
        {visible.map((candidate) => (
          <VoteRow
            key={candidate.playerId}
            candidate={candidate}
            votes={votesByPlayer.get(candidate.playerId) ?? 0}
            total={total}
            mine={candidate.playerId === myVote}
            leading={candidate.playerId === leaderId}
            disabled={vote.isPending}
            onVote={onVote}
          />
        ))}
      </View>

      {candidates.length > COLLAPSED_COUNT ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => setExpanded((value) => !value)}
          className="flex-row items-center justify-center gap-1 active:opacity-70"
        >
          <AppText variant="micro" weight="bold" className="text-volt-bright">
            {expanded ? t('potm.showLess') : t('potm.showAll')}
          </AppText>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={13}
            color={palette.voltBright}
          />
        </Pressable>
      ) : null}

      {myVote ? (
        <View className="flex-row items-center justify-center gap-1.5">
          <Ionicons name="checkmark-circle" size={13} color={palette.win} />
          <AppText variant="micro" className="text-soft">
            {t('potm.changeHint')}
          </AppText>
        </View>
      ) : total === 0 ? (
        <AppText variant="micro" className="text-center text-muted">
          {t('potm.beFirst')}
        </AppText>
      ) : null}
    </Card>
  );
}
